// These tags are stripped completely (including their content) — never unwrapped
const STRIP_TAGS = new Set(['script', 'style', 'noscript', 'template']);

const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'figcaption', 'figure',
  'h1', 'h2', 'h3', 'h4', 'hr', 'i', 'iframe', 'img', 'li', 'ol', 'p',
  'pre', 's', 'span', 'strong', 'u', 'ul',
]);

const GLOBAL_ATTRS = new Set(['class', 'id', 'title', 'aria-label', 'data-youtube-video']);
const TAG_ATTRS = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  iframe: new Set(['src', 'title', 'width', 'height', 'allow', 'allowfullscreen', 'frameborder']),
};

const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;
const SAFE_IMAGE = /^(https?:|data:image\/(png|jpe?g|webp|gif);base64,)/i;
const SAFE_IFRAME = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com)\//i;

const unwrap = (node) => {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) parent.insertBefore(node.firstChild, node);
  parent.removeChild(node);
};

const attrAllowed = (tag, name) => GLOBAL_ATTRS.has(name) || TAG_ATTRS[tag]?.has(name);

export function sanitizeHtml(html = '') {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/\son\w+="[^"]*"/gi, '')
      .replace(/\s(href|src)="javascript:[^"]*"/gi, '');
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  for (const node of nodes.reverse()) {
    const tag = node.tagName.toLowerCase();
    if (STRIP_TAGS.has(tag)) {
      node.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      unwrap(node);
      continue;
    }

    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();
      if (name.startsWith('on') || name === 'style' || !attrAllowed(tag, name)) {
        node.removeAttribute(attr.name);
        continue;
      }
      if (tag === 'a' && name === 'href' && !SAFE_URL.test(value)) node.removeAttribute(attr.name);
      if (tag === 'img' && name === 'src' && !SAFE_IMAGE.test(value)) node.removeAttribute(attr.name);
      if (tag === 'iframe' && name === 'src' && !SAFE_IFRAME.test(value)) node.removeAttribute(attr.name);
    }

    if (tag === 'a' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (tag === 'iframe' && !node.getAttribute('src')) {
      node.remove();
    }
  }

  return root.innerHTML;
}
